// Google Analytics utility functions for PrismX Landing Page
// Focused on conversion tracking only

export const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_ID || '';

// Helper: Check if gtag is available
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && !!(window as any).gtag;
};

// Helper: Log events in development mode
const devLog = (eventName: string, params: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 GA Event:', eventName, params);
  }
};

// Generic event tracking function
export const trackEvent = (
  eventName: string,
  eventParams?: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  }
) => {
  if (isGtagAvailable()) {
    (window as any).gtag('event', eventName, eventParams);
  } else {
    devLog(eventName, eventParams);
  }
};

// Track page views
export const trackPageView = (url: string) => {
  if (isGtagAvailable()) {
    (window as any).gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// ========================================
// CRITICAL CONVERSION EVENTS
// ========================================

/**
 * PRIMARY CONVERSION: Chrome Store Click
 * Track when user clicks "Add to Chrome" button
 */
export const trackChromeStoreClick = (location: string) => {
  trackEvent('chrome_store_click', {
    event_category: 'conversion',
    event_label: location, // 'hero' or 'final_cta'
    value: 1,
  });
  
  // Also track as a conversion
  trackEvent('conversion', {
    event_category: 'conversion',
    event_label: 'chrome_store_click',
    value: 1,
  });
};

/**
 * ENGAGEMENT CONVERSION: Demo Video Play
 * Track when user plays the demo video
 */
export const trackDemoVideoPlay = () => {
  trackEvent('demo_video_play', {
    event_category: 'engagement',
    event_label: 'hero_video',
    value: 1,
  });
};

/**
 * SESSION TRACKING: Track session start
 */
export const trackSessionStart = () => {
  trackEvent('session_start', {
    event_category: 'session',
    event_label: 'page_load',
  });
};

// ========================================
// CONVERSION FUNNEL TRACKING
// ========================================

/**
 * Track funnel step completion
 * Helps analyze drop-off points in user journey
 */
export const trackFunnelStep = (stepNumber: number, stepName: string) => {
  trackEvent('funnel_step', {
    event_category: 'funnel',
    event_label: `step_${stepNumber}_${stepName}`,
    value: stepNumber,
  });
};

/**
 * Complete conversion funnel:
 * 1. Page Load (visitor arrives)
 * 2. Hero View (sees main value prop)
 * 3. Features View (scrolls to features)
 * 4. Demo/Comparison View (engages with content)
 * 5. CTA Click (clicks "Add to Chrome")
 */
export const ConversionFunnel = {
  step1_PageLoad: () => trackFunnelStep(1, 'page_load'),
  step2_HeroView: () => trackFunnelStep(2, 'hero_view'),
  step3_FeaturesView: () => trackFunnelStep(3, 'features_view'),
  step4_DemoOrComparison: (type: 'demo' | 'comparison') => 
    trackFunnelStep(4, `${type}_view`),
  step5_CTAClick: () => trackFunnelStep(5, 'cta_click'),
};

// ========================================
// EXPORT ALL AS OBJECT
// ========================================

export const Analytics = {
  // Core tracking
  trackEvent,
  trackPageView,
  
  // Conversions
  trackChromeStoreClick,
  trackDemoVideoPlay,
  
  // Session
  trackSessionStart,
  
  // Funnel
  ConversionFunnel,
  trackFunnelStep,
};

export default Analytics;
